import { DatePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { QuestionCatalogService } from '../../services/question-catalog.service';
import { QuestionCatalogItem, QuestionField, QuestionPoolDetail } from '../../models/question-catalog.model';

@Component({
  selector: 'app-question-table-detail',
  standalone: true,
  imports: [RouterLink, FormsModule, DatePipe],
  template: `
    <section class="section question-table-page">
      @if (auth.isAdmin()) {
        @if (isLoading()) {
          <article class="card accent-card no-access-card">
            <h2>Lade Tabelle...</h2>
          </article>
        } @else if (errorMessage()) {
          <article class="card accent-card no-access-card">
            <h2>Fehler</h2>
            <p>{{ errorMessage() }}</p>
            <a routerLink="/fragenkatalog" class="button primary">Zurück</a>
          </article>
        } @else if (detail()) {
          <div class="question-detail-topline">
            <a routerLink="/fragenkatalog" class="back-link">← Zurück zum Fragenkatalog</a>
          </div>

          <div class="question-title-row">
            <div class="page-title-block question-table-heading">
              <h1>{{ detail()!.summary.title.toUpperCase() }}</h1>
              <span class="title-line"></span>
            </div>
            <button class="button primary" type="button" (click)="openCreate()">Neue Frage hinzufügen</button>
          </div>

          <section class="question-table-overview compact-overview card accent-card">
            <div>
              <small>Fragen</small>
              <strong>{{ detail()!.summary.totalQuestions }}</strong>
            </div>
            <div>
              <small>Gespielt</small>
              <strong>{{ detail()!.summary.playedQuestions }}</strong>
            </div>
            <div>
              <small>Nicht verwendet</small>
              <strong>{{ detail()!.summary.unusedQuestions }}</strong>
            </div>
          </section>

          <section class="question-list">
            @for (question of detail()!.questions; track question.id) {
              <article class="question-row card accent-card" [class.played]="question.isPlayed">
                <button class="button secondary compact-button edit-question-button" type="button" (click)="startEdit(question)">Bearbeiten</button>

                <div class="question-row-main">
                  <p class="tag">{{ getFieldValue(question, 'category') || 'Ohne Kategorie' }} · {{ question.isPlayed ? playedResultLabel(question) : 'Nicht verwendet' }}</p>
                  <h2>{{ question.question }}</h2>

                  <div class="question-data-grid">
                    @for (field of editableFields(); track field.name) {
                      <div>
                        <span>{{ field.name }}</span>
                        <strong>{{ formatValue(getFieldValue(question, field.name)) }}</strong>
                      </div>
                    }
                  </div>
                </div>

                <div class="question-row-side">
                  @if (question.playedShowTitle) {
                    <div><span>Gespielt in</span><p>{{ question.playedShowTitle }}</p></div>
                  }
                  @if (question.playedAt) {
                    <div><span>Gespielt am</span><p>{{ question.playedAt | date:'dd.MM.yyyy' }}</p></div>
                  }
                  @if (question.isPlayed) {
                    <div><span>Beantwortung</span><p>{{ playedResultLabel(question) }}</p></div>
                  }
                  @if (question.answerAccuracyPercent !== null && question.answerAccuracyPercent !== undefined) {
                    <div><span>Trefferquote</span><p>{{ question.answerAccuracyPercent }}%</p></div>
                  }
                </div>
              </article>
            } @empty {
              <article class="card accent-card no-access-card">
                <h2>Noch keine Fragen</h2>
                <p>Für diese Spielshow-Tabelle sind noch keine Fragen vorhanden.</p>
              </article>
            }
          </section>

          @if (editorOpen()) {
            <div class="question-modal-backdrop" (click)="closeEditor()">
              <section class="question-modal card accent-card" (click)="$event.stopPropagation()">
                <div class="question-editor-head modal-head">
                  <div>
                    <small>{{ editMode() ? 'Frage bearbeiten' : 'Neue Frage' }}</small>
                    <h2>{{ editMode() ? 'Eintrag aktualisieren' : 'Frage hinzufügen' }}</h2>
                  </div>
                  <button class="modal-close" type="button" (click)="closeEditor()" aria-label="Fenster schließen">×</button>
                </div>

                <form class="dynamic-question-form modal-form" (ngSubmit)="saveQuestion()">
                  @for (field of editableFields(); track field.name) {
                    <label [class.full-field]="field.type === 'text' && shouldUseTextarea(field.name)">
                      <span>{{ field.name }}</span>
                      <small>{{ field.label }}{{ field.required ? ' *' : '' }}</small>

                      @if (field.type === 'select' && field.name === 'difficulty') {
                        <select [(ngModel)]="formData[field.name]" [name]="field.name" [required]="field.required">
                          <option value="easy">easy</option>
                          <option value="medium">medium</option>
                          <option value="hard">hard</option>
                        </select>
                      } @else if (field.type === 'number') {
                        <input type="number" [(ngModel)]="formData[field.name]" [name]="field.name" [required]="field.required" />
                      } @else if (shouldUseTextarea(field.name)) {
                        <textarea rows="2" [(ngModel)]="formData[field.name]" [name]="field.name" [required]="field.required"></textarea>
                      } @else {
                        <input type="text" [(ngModel)]="formData[field.name]" [name]="field.name" [required]="field.required" />
                      }
                    </label>
                  }

                  <div class="form-actions modal-actions">
                    <button class="button primary" type="submit" [disabled]="isSaving()">
                      {{ isSaving() ? 'Speichert...' : editMode() ? 'Änderungen speichern' : 'Neue Frage hinzufügen' }}
                    </button>
                    <button class="button secondary" type="button" (click)="closeEditor()">Schließen</button>
                    @if (saveMessage()) {
                      <span class="save-message">{{ saveMessage() }}</span>
                    }
                  </div>
                </form>
              </section>
            </div>
          }
        }
      } @else {
        <article class="card accent-card no-access-card">
          <h2>Kein Zugriff</h2>
          <p>Der Fragenkatalog ist aktuell nur für Admins sichtbar.</p>
          <a routerLink="/login" class="button primary">Einloggen</a>
        </article>
      }
    </section>
  `
})
export class QuestionTableDetailComponent implements OnInit {
  readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly catalog = inject(QuestionCatalogService);

  readonly tableId = this.route.snapshot.paramMap.get('id') ?? '';
  readonly detail = signal<QuestionPoolDetail | null>(null);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly errorMessage = signal('');
  readonly saveMessage = signal('');
  readonly editingQuestionId = signal<number | null>(null);
  readonly editorOpen = signal(false);

  formData: Record<string, string | number | null> = {};

  readonly editableFields = computed<QuestionField[]>(() => this.detail()?.fields ?? []);
  readonly editMode = computed(() => this.editingQuestionId() !== null);

  ngOnInit(): void {
    if (this.auth.isAdmin()) {
      this.loadDetail();
    }
  }

  loadDetail(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.catalog.getPoolDetail(this.tableId).subscribe({
      next: detail => {
        this.detail.set(detail);
        if (!this.editorOpen()) {
          this.resetForm();
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.errorMessage.set('Die Fragen-Tabelle konnte nicht geladen werden. Prüfe Backend und Pool-ID.');
        this.isLoading.set(false);
      }
    });
  }

  openCreate(): void {
    this.resetForm();
    this.editorOpen.set(true);
  }

  closeEditor(): void {
    this.editorOpen.set(false);
    this.resetForm();
  }

  saveQuestion(): void {
    if (!this.detail()) {
      return;
    }

    const data = this.buildRequestData();
    const questionId = this.editingQuestionId();
    this.isSaving.set(true);
    this.saveMessage.set('');

    const request$ = questionId === null
      ? this.catalog.createQuestion(this.tableId, data)
      : this.catalog.updateQuestion(this.tableId, questionId, data);

    request$.subscribe({
      next: () => {
        this.saveMessage.set(questionId === null ? 'Frage wurde erstellt.' : 'Frage wurde gespeichert.');
        this.isSaving.set(false);
        this.editorOpen.set(false);
        this.loadDetail();
      },
      error: error => {
        this.saveMessage.set(error?.error?.message ?? 'Speichern fehlgeschlagen.');
        this.isSaving.set(false);
      }
    });
  }

  startEdit(question: QuestionCatalogItem): void {
    this.editingQuestionId.set(question.id);
    this.saveMessage.set('');
    this.formData = {};

    for (const field of this.editableFields()) {
      const value = question.data[field.name];
      this.formData[field.name] = value === undefined || value === null ? null : String(value);
    }

    this.editorOpen.set(true);
  }

  resetForm(): void {
    this.editingQuestionId.set(null);
    this.saveMessage.set('');
    this.formData = {};

    for (const field of this.editableFields()) {
      if (field.name === 'difficulty') {
        this.formData[field.name] = 'medium';
      } else {
        this.formData[field.name] = null;
      }
    }
  }


  playedResultLabel(question: QuestionCatalogItem): string {
    if (!question.isPlayed) {
      return 'Nicht verwendet';
    }

    if (question.wasAnsweredCorrectly === true) {
      return 'Richtig beantwortet';
    }

    if (question.wasAnsweredCorrectly === false) {
      return 'Falsch beantwortet';
    }

    return 'Gespielt';
  }

  getFieldValue(question: QuestionCatalogItem, fieldName: string): unknown {
    return question.data[fieldName];
  }

  formatValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    if (typeof value === 'boolean') {
      return value ? 'Ja' : 'Nein';
    }

    return String(value);
  }

  shouldUseTextarea(fieldName: string): boolean {
    return ['question_text', 'solution_text', 'hint_1', 'hint_2', 'description', 'task_description', 'forbidden_words'].includes(fieldName);
  }

  private buildRequestData(): Record<string, unknown> {
    const data: Record<string, unknown> = {};

    for (const field of this.editableFields()) {
      const rawValue = this.formData[field.name];

      if (field.type === 'number') {
        data[field.name] = rawValue === null || rawValue === '' ? null : Number(rawValue);
      } else {
        data[field.name] = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
      }
    }

    return data;
  }
}
